import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ArrowLeft, Eye, Lock } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

const HEARTBEAT_MS = 5000;

export default function TrainingWatch() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [a, setA] = useState(null);
    const [err, setErr] = useState("");
    const [visible, setVisible] = useState(!document.hidden);
    const watchedRef = useRef(0);
    const iframeRef = useRef();

    const load = async () => {
        try {
            const { data } = await api.get(`/training/assignments/${id}`);
            setA(data.assignment);
            watchedRef.current = data.assignment.seconds_watched || 0;
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    // Visibility detection
    useEffect(() => {
        const handler = () => setVisible(!document.hidden);
        document.addEventListener("visibilitychange", handler);
        return () => document.removeEventListener("visibilitychange", handler);
    }, []);

    // Heartbeat — only ticks when visible
    useEffect(() => {
        if (!a || a.status === "complete") return;
        const t = setInterval(async () => {
            try {
                if (visible) {
                    watchedRef.current += HEARTBEAT_MS / 1000;
                }
                const { data } = await api.post(
                    `/training/assignments/${a.id}/heartbeat`,
                    { seconds_watched: watchedRef.current, visible },
                );
                setA(data);
                if (data.status === "complete") clearInterval(t);
            } catch (e) {
                /* swallow */
            }
        }, HEARTBEAT_MS);
        return () => clearInterval(t);
    }, [a?.id, a?.status, visible]);

    if (!a) {
        return (
            <div className="page-shell" data-testid="tw-loading">
                {err ? (
                    <div className="brand-card" style={{ borderColor: "var(--brand-red)" }}>
                        {err}
                    </div>
                ) : (
                    <p className="text-muted">Loading…</p>
                )}
            </div>
        );
    }

    const m = a.module || {};
    const pct = a.percent || 0;
    const done = a.status === "complete";
    const bunnyUrl = m.bunny_library_id && m.bunny_video_id
        ? `https://iframe.mediadelivery.net/embed/${m.bunny_library_id}/${m.bunny_video_id}?autoplay=true&preload=true&loop=false&showSpeed=false`
        : null;

    return (
        <div className="page-shell">
            <Link
                to="/training"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="tw-back"
            >
                <ArrowLeft size={14} />
                Back to training
            </Link>
            <header className="fade-up mt-4">
                <p className="eyebrow">Training · <span className="capitalize">{m.topic}</span></p>
                <h1 className="h1 mt-3" data-testid="tw-title">{m.title}</h1>
                <p className="text-muted mt-2 max-w-[640px]">{m.description}</p>
            </header>

            <div className="brand-card mt-5 fade-up" data-testid="tw-player">
                {bunnyUrl ? (
                    <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
                        <iframe
                            ref={iframeRef}
                            src={bunnyUrl}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={m.title}
                        />
                    </div>
                ) : (
                    <div
                        className="rounded-md p-10 text-center"
                        style={{ background: "var(--brand-dark)", color: "#fff" }}
                        data-testid="tw-no-video"
                    >
                        <Lock size={28} style={{ color: "var(--brand-red)" }} className="mx-auto" />
                        <p className="font-display font-bold mt-3">
                            Video not yet configured
                        </p>
                        <p className="text-sm opacity-70 mt-2">
                            Your admin needs to set the Bunny library and video ID.
                            Heartbeat-based completion is still wired — admin can mark complete from the module.
                        </p>
                    </div>
                )}

                {/* Progress */}
                <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                        <p className="eyebrow">Progress · {pct.toFixed(0)}%</p>
                        <div className="flex items-center gap-3 text-xs text-muted">
                            <span className="inline-flex items-center gap-1">
                                <Eye size={12} />
                                {visible ? "Visible" : "Tab hidden"}
                            </span>
                            {a.tab_blur_count > 0 && (
                                <span className="badge-pill badge-warning">
                                    {a.tab_blur_count} away
                                </span>
                            )}
                        </div>
                    </div>
                    <div
                        className="h-2 rounded-full overflow-hidden"
                        style={{ background: "var(--brand-surface)" }}
                        data-testid="tw-progress"
                    >
                        <div
                            className="h-full transition-all"
                            style={{
                                width: `${pct}%`,
                                background: done ? "var(--brand-success)" : "var(--brand-red)",
                            }}
                        />
                    </div>
                    {!visible && !done && (
                        <p
                            className="text-xs mt-2 flex items-center gap-2"
                            style={{ color: "var(--brand-warning)" }}
                            data-testid="tw-tab-warning"
                        >
                            <AlertTriangle size={12} />
                            Switch back to this tab to keep earning credit.
                        </p>
                    )}
                </div>

                {done && (
                    <div
                        className="brand-card mt-5"
                        style={{ background: "rgba(46,125,50,0.08)", borderColor: "rgba(46,125,50,0.4)" }}
                        data-testid="tw-complete"
                    >
                        <CheckCircle2 size={20} style={{ color: "var(--brand-success)" }} className="inline mr-2" />
                        Training complete. Logged on {new Date(a.completed_at).toLocaleString()}.
                        <button
                            type="button"
                            className="btn btn-secondary ml-3"
                            onClick={() => navigate("/training")}
                            data-testid="tw-done-back"
                        >
                            Back to my training
                        </button>
                    </div>
                )}

                {m.script && (
                    <details className="mt-5" data-testid="tw-script">
                        <summary className="cursor-pointer link-red text-sm">
                            View written script
                        </summary>
                        <div
                            className="mt-3 p-4 rounded-md whitespace-pre-wrap text-sm"
                            style={{ background: "var(--brand-cream)" }}
                        >
                            {m.script}
                        </div>
                    </details>
                )}
            </div>
        </div>
    );
}
